import { Controller } from '@hotwired/stimulus'

// Define the types for the targets to help TypeScript infer them
export default class extends Controller {
  static targets: string[] = ['riskCategory', 'riskType', 'riskSource', 'sourceCause']

  // Define the types for the controller targets
  declare readonly riskCategoryTarget: HTMLSelectElement
  declare readonly riskTypeTarget: HTMLSelectElement
  declare readonly riskSourceTarget: HTMLSelectElement
  declare readonly sourceCauseTarget: HTMLSelectElement
  declare readonly hasRiskTypeTarget: boolean
  declare readonly hasRiskSourceTarget: boolean
  declare readonly hasSourceCauseTarget: boolean

  connect (): void {
    this.setupEventListeners()
  }

  setupEventListeners (): void {
    if (this.hasRiskTypeTarget) { // Explicit check for truthiness
      this.riskTypeTarget.addEventListener('change', (event) => {
        void this.updateRiskCategory(event) // Explicitly mark the promise as ignored
      })
    }
    if (this.hasRiskSourceTarget) { // Explicit check for truthiness
      this.riskSourceTarget.addEventListener('change', (event) => {
        void this.updateSourceCauses(event) // Explicitly mark the promise as ignored
      })
    }
    if (this.hasSourceCauseTarget) { // Explicit check for truthiness
      this.sourceCauseTarget.addEventListener('change', (event) => {
        void this.updateRiskSource(event) // Explicitly mark the promise as ignored
      })
    }
  }

  async updateRiskSource (event: Event): Promise<void> {
    const selectedSourceCauseId: string = this.sourceCauseTarget.value
    if (selectedSourceCauseId === '') return

    await fetch(`/risks/update_risk_source/${selectedSourceCauseId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
      },
      body: JSON.stringify({ source_cause_id: selectedSourceCauseId })
    })
  }

  // Method to update the risk type based on selected category
  async updateRiskCategory (event: Event): Promise<void> {
    const selectedTypeId: string = this.riskTypeTarget.value
    if (selectedTypeId === '') {
      const riskCategoryField = this.riskCategoryTarget
      riskCategoryField.innerHTML = ''
      return
    }

    try {
      const response: Response = await fetch(`/risks/risk_types_for_category/${selectedTypeId}`)
      const data: { risk_categories: Array<{ id: number, name: string }> } = await response.json()

      const riskCategoryField = this.riskCategoryTarget
      riskCategoryField.innerHTML = ''

      data.risk_categories?.forEach((category) => {
        const option = document.createElement('option')
        option.value = category.id.toString() // Ensure id is a string for the value
        option.textContent = category.name
        riskCategoryField.appendChild(option)
      })
    } catch (error) {
      console.error('Error fetching risk types:', error)
    }
  }

  // Method to update the source causes based on selected source
  async updateSourceCauses (event: Event): Promise<void> {
    const selectedSourceId: string = this.riskSourceTarget.value
    if (selectedSourceId === '') {
      const sourceCauseSelect = this.sourceCauseTarget
      sourceCauseSelect.innerHTML = ''
      return
    }// Explicit empty string check

    try {
      const response: Response = await fetch(`/risks/source_causes_for_source/${selectedSourceId}`)
      const data: { source_causes: Array<{ id: number, name: string }> } = await response.json()

      const sourceCauseSelect = this.sourceCauseTarget
      sourceCauseSelect.innerHTML = '' // Clear options

      data.source_causes?.sort((a, b) => a.name.localeCompare(b.name))
      data.source_causes?.forEach((cause) => {
        const option = document.createElement('option')
        option.value = cause.id.toString() // Ensure id is a string for the value
        option.textContent = cause.name
        sourceCauseSelect.appendChild(option)
      })
    } catch (error) {
      console.error('Error fetching source causes:', error)
    }
  }
}
